import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HelpCircle, Send, ArrowLeft, MessageSquare, Pin, 
  CheckCircle, Clock, Filter, Search, ChevronDown, 
  ChevronUp, User, Users, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HelpDeskPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAskModal, setShowAskModal] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [questionCategory, setQuestionCategory] = useState('general');
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [stats, setStats] = useState(null);
  const [answerText, setAnswerText] = useState({});
  const [faqs, setFaqs] = useState([]);
  const [expandedFaqs, setExpandedFaqs] = useState({});

  const categories = [
    { id: 'general', label: 'General', icon: '💬' },
    { id: 'schedule', label: 'Schedule/Time Off', icon: '📅' },
    { id: 'payroll', label: 'Payroll/Pay', icon: '💰' },
    { id: 'worksite', label: 'Work Site', icon: '🏢' },
    { id: 'project', label: 'Summer Project', icon: '📋' },
    { id: 'other', label: 'Other', icon: '❓' }
  ];

  useEffect(() => {
    fetchQuestions();
    fetchFAQsData();
    if (user.role === 'lead_monitor') {
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  
  const fetchFAQsData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/faq`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setFaqs(await res.json());
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/helpdesk/questions?filter=${filter}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setQuestions(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);

  const fetchFAQs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/faq`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setFaqs(await res.json());
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/helpdesk/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const submitQuestion = async () => {
    if (!newQuestion.trim()) {
      toast.error('Please enter your question');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/helpdesk/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          question: newQuestion,
          category: questionCategory
        })
      });

      if (res.ok) {
        toast.success('Question submitted! Your lead monitor will respond soon.');
        setShowAskModal(false);
        setNewQuestion('');
        setQuestionCategory('general');
        fetchQuestions();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit question');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const submitAnswer = async (questionId) => {
    const answer = answerText[questionId];
    if (!answer?.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/helpdesk/questions/${questionId}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          answer: answer,
          is_pinned: false
        })
      });

      if (res.ok) {
        toast.success('Answer submitted!');
        setAnswerText({ ...answerText, [questionId]: '' });
        fetchQuestions();
        if (user.role === 'lead_monitor') fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit answer');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const togglePin = async (questionId) => {
    try {
      const res = await fetch(`${API_URL}/api/helpdesk/questions/${questionId}/pin`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.is_pinned ? 'Question pinned!' : 'Question unpinned');
        fetchQuestions();
      }
    } catch (error) {
      toast.error('Failed to update pin status');
    }
  };

  const toggleExpand = (id) => {
    setExpandedQuestions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredQuestions = questions.filter(q => 
    q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (q.answer && q.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || categories[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: colors?.background || '#FEF9F3' }}>
      {/* Header */}
      <div className="p-6 rounded-b-3xl" style={{ background: colors?.header_gradient || colors?.header || '#1B2D4F', color: '#fff' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">


        {/* FAQs Section */}
        {faqs.length > 0 && (
          <Card className="p-6 mb-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
                <HelpCircle className="w-6 h-6" style={{ color: '#3B82F6' }} />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: colors.text }}>Frequently Asked Questions</h2>
                <p className="text-sm" style={{ color: colors.text_secondary }}>Quick answers to common questions</p>
              </div>
            </div>

            <div className="space-y-3">
              {faqs.map(faq => (
                <Card
                  key={faq.id}
                  className="p-4 cursor-pointer hover:shadow-md transition-all"
                  style={{ backgroundColor: colors.surface, borderColor: colors.border }}
                  onClick={() => setExpandedFaqs(prev => ({ ...prev, [faq.id]: !prev[faq.id] }))}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-bold mb-1" style={{ color: colors.text }}>❓ {faq.question}</p>
                      {expandedFaqs[faq.id] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-3 pt-3"
                          style={{ borderTop: `1px solid ${colors.border}` }}
                        >
                          <p className="text-sm" style={{ color: colors.text }}>✅ {faq.answer}</p>
                        </motion.div>
                      )}
                    </div>
                    <Badge className="ml-2 bg-blue-500/10 text-blue-600 font-semibold">
                      {expandedFaqs[faq.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Badge>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-gray-900 hover:bg-white/20 rounded-full"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="flex items-center gap-3">
              <HelpCircle className="w-10 h-10" />
              <div>
                <h1 className="font-heading text-3xl font-bold">Help Desk</h1>
                <p className="opacity-90">Get answers to your questions</p>
              </div>
            </div>
          </div>

          {/* Lead Monitor Stats */}
          {user.role === 'lead_monitor' && stats && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="bg-gray-100 backdrop-blur rounded-xl p-3 text-center">
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-xs opacity-75">Pending</p>
              </div>
              <div className="bg-gray-100 backdrop-blur rounded-xl p-3 text-center">
                <p className="text-2xl font-bold">{stats.answered}</p>
                <p className="text-xs opacity-75">Answered</p>
              </div>
              <div className="bg-gray-100 backdrop-blur rounded-xl p-3 text-center">
                <p className="text-2xl font-bold">{stats.pinned}</p>
                <p className="text-xs opacity-75">Pinned</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Search & Filter */}
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl bg-white"
            data-testid="search-questions"
          />
        </div>

        {/* Filter Tabs */}
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="w-full bg-white rounded-2xl p-1 shadow-sm">
            <TabsTrigger value="all" className="flex-1 rounded-xl" data-testid="filter-all">
              <Sparkles className="w-4 h-4 mr-1" /> All
            </TabsTrigger>
            {user.role !== 'lead_monitor' && (
              <TabsTrigger value="mine" className="flex-1 rounded-xl" data-testid="filter-mine">
                <User className="w-4 h-4 mr-1" /> My Qs
              </TabsTrigger>
            )}
            {user.role === 'lead_monitor' && (
              <TabsTrigger value="pending" className="flex-1 rounded-xl" data-testid="filter-pending">
                <Clock className="w-4 h-4 mr-1" /> Pending
              </TabsTrigger>
            )}
            <TabsTrigger value="pinned" className="flex-1 rounded-xl" data-testid="filter-pinned">
              <Pin className="w-4 h-4 mr-1" /> Pinned
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Ask a Question Button (for youth/parents) */}
        {(user.role === 'youth' || user.role === 'parent') && (
          <Dialog open={showAskModal} onOpenChange={setShowAskModal}>
            <DialogTrigger asChild>
              <Button 
                className="w-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 h-12"
                data-testid="ask-question-btn"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Ask a Question
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Ask a Question</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Category</label>
                  <Select value={questionCategory} onValueChange={setQuestionCategory}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Your Question</label>
                  <Textarea
                    placeholder="Type your question here..."
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    className="rounded-xl min-h-[100px]"
                    data-testid="question-input"
                  />
                </div>
                <Button 
                  onClick={submitQuestion}
                  className="w-full rounded-full bg-blue-500 hover:bg-blue-600"
                  data-testid="submit-question-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit Question
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Questions List */}
      <div className="max-w-4xl mx-auto px-4 space-y-4">
        {filteredQuestions.length === 0 ? (
          <Card className="p-8 text-center rounded-3xl">
            <HelpCircle className="w-16 h-16 text-blue-400 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">No Questions Yet</h2>
            <p className="text-muted-foreground">
              {user.role === 'lead_monitor' 
                ? "No questions from your group yet. Check back later!"
                : "Have a question? Tap 'Ask a Question' above!"}
            </p>
          </Card>
        ) : (
          <AnimatePresence>
            {filteredQuestions.map((q, index) => {
              const category = getCategoryInfo(q.category);
              const isExpanded = expandedQuestions[q.id];
              const isOwnQuestion = q.asked_by_id === user.id;
              
              return (
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className={`rounded-3xl overflow-hidden ${
                      q.is_pinned ? 'border-2 border-yellow-400 bg-yellow-50' : 'bg-white'
                    }`}
                    data-testid={`question-card-${q.id}`}
                  >
                    {/* Question Header */}
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => toggleExpand(q.id)}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10 ring-2 ring-blue-100 flex-shrink-0">
                          {q.asked_by_picture ? (
                            <AvatarImage src={q.asked_by_picture} alt={q.asked_by_name} />
                          ) : null}
                          <AvatarFallback className="bg-blue-100 text-blue-700 font-bold">
                            {q.asked_by_avatar || q.asked_by_name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge variant="outline" className="text-xs">
                              {category.label}
                            </Badge>
                            {q.is_pinned && (
                              <Badge className="bg-yellow-500 text-gray-900 text-xs">
                                <Pin className="w-3 h-3 mr-1" /> Pinned
                              </Badge>
                            )}
                            <Badge className={q.status === 'answered' ? 'bg-green-500' : 'bg-orange-500'}>
                              {q.status === 'answered' ? (
                                <><CheckCircle className="w-3 h-3 mr-1" /> Answered</>
                              ) : (
                                <><Clock className="w-3 h-3 mr-1" /> Pending</>
                              )}
                            </Badge>
                          </div>
                          <p className="font-medium text-white">{q.question}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Asked by {isOwnQuestion ? 'You' : q.asked_by_name} • {new Date(q.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>

                    {/* Expanded Content */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t"
                        >
                          <div className="p-4 space-y-4">
                            {/* Answer Section */}
                            {q.answer ? (
                              <div className="bg-green-50 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                  <span className="font-semibold text-green-800">Answer from {q.answered_by_name}</span>
                                </div>
                                <p className="text-gray-700">{q.answer}</p>
                                <p className="text-xs text-gray-500 mt-2">
                                  Answered {new Date(q.answered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                              </div>
                            ) : user.role === 'lead_monitor' ? (
                              <div className="space-y-3">
                                <Textarea
                                  placeholder="Type your answer..."
                                  value={answerText[q.id] || ''}
                                  onChange={(e) => setAnswerText({ ...answerText, [q.id]: e.target.value })}
                                  className="rounded-xl"
                                  data-testid={`answer-input-${q.id}`}
                                />
                                <Button 
                                  onClick={() => submitAnswer(q.id)}
                                  className="rounded-full bg-green-500 hover:bg-green-600"
                                  data-testid={`submit-answer-${q.id}`}
                                >
                                  <Send className="w-4 h-4 mr-2" />
                                  Submit Answer
                                </Button>
                              </div>
                            ) : (
                              <div className="bg-orange-50 rounded-xl p-4 text-center">
                                <Clock className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                                <p className="text-orange-800 font-medium">Waiting for response</p>
                                <p className="text-sm text-orange-600">Your lead monitor will respond soon!</p>
                              </div>
                            )}

                            {/* Lead Monitor Actions */}
                            {user.role === 'lead_monitor' && (
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); togglePin(q.id); }}
                                  className="rounded-full"
                                  data-testid={`pin-btn-${q.id}`}
                                >
                                  <Pin className={`w-4 h-4 mr-1 ${q.is_pinned ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                                  {q.is_pinned ? 'Unpin' : 'Pin'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      <Navigation role="youth" />
    </div>
  );
};

export default HelpDeskPage;
